import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { applySearchFilter } from '@/redux/modules/search';

import { aiSearchToParams, buildRemovalParams } from './aiSearchToParams';
import type { IAiSearchResponse } from './client';

/**
 * Applies an AI-search extractor result to the live /rv-search filters.
 * Returns a callback that maps the structured filters to URL params and
 * dispatches applySearchFilter — which pushes the URL, syncs query state,
 * and refetches results (the existing facet pills update automatically).
 *
 * Intent drives the merge:
 *   narrow / replace_field → merge over current params (later keys
 *     overwrite, so a replaced field swaps in place)
 *   replace_all / fresh    → ignore existing params and start clean
 */
export function useApplyAiSearch() {
  const dispatch = useDispatch();
  // Live query params — needed to clear specific facets (e.g. drop only
  // pet_friendly from the filter[feature] list) on a removal.
  const currentParams = useSelector(
    (state: { queryParams: Record<string, string | string[] | undefined> }) => state.queryParams,
  );

  return useCallback(
    (resp: IAiSearchResponse): boolean => {
      // A question/handoff isn't a search — callers route those to the help
      // panel, but guard here too so a non-search response can never touch the
      // filters.
      if (resp.outcome && resp.outcome !== 'search') return false;

      const params = aiSearchToParams(resp.filters);
      // Removals come back as a `remove` array of facet names; map them to
      // empty-string params so applySearchFilter deletes those keys.
      if (resp.remove?.length) {
        Object.assign(params, buildRemovalParams(resp.remove, currentParams || {}));
      }
      // Vibes/tags are soft — never sent as URL filters — but they still mean
      // the extractor understood a trip ("beach trip" → adventure/comfort), so
      // a soft-only response proceeds: it navigates to results and the vibes
      // drive the results heading via recordSearch.
      const hasSoftSignals = Boolean(resp.filters?.vibes?.length || resp.filters?.tags?.length);
      // A truly empty extraction (nothing understood at all) must not reset
      // the live search — a fresh/replace_all with no params would otherwise
      // wipe it. Returns false so the caller can stop its loading state and
      // tell the renter nothing was understood instead of waiting on a
      // navigation that will never happen.
      if (Object.keys(params).length === 0 && !hasSoftSignals) return false;

      const reset = resp.intent === 'replace_all' || resp.intent === 'fresh';
      // Positional args mirror applySearchFilter's signature; the 6th
      // (ignoreExistingQueryParams) is what makes replace_all a clean reset.
      dispatch(applySearchFilter(params, false, false, false, undefined, reset));
      return true;
    },
    [dispatch, currentParams],
  );
}
